<script>
  import _ from 'lodash';
  import axios from 'axios';

  export default ({
    name: 'v-form-step-group',

    props: {
      refFormUrl: {
        type: String,
        default: '',
      },
      refTransitionClass: {
        type: String,
        default: 'p-stepForm__step',
      },
      refFirstActiveIndex: {
        type: Number,
        default: 1,
      },
      refShowStepLength: {
        type: Number,
        default: null,
      },
      refAutoScrollOnStepChange: {
        type: Boolean,
        default: false,
      },
      refAutoScrollToNextBtn: {
        type: Boolean,
        default: false,
      },
      refShowFollowIcon: {
        type: Boolean,
        default: false,
      },
      refShowInitialModal: {
        type: Boolean,
        default: true,
      },
      refShowAddressField: {
        type: Boolean,
        default: false,
      },
      refTargetPrefectures: {
        type: Array,
      },
      refGaClassNames: {
        type: Array,
      },
      refActiveStepBars: {
        type: Boolean,
        default: false,
      },
    },

    data() {
      return {
        steps: [],
        activeIndex: 1,
        inputLength: 9,
        selectedList: [],
        activeSteps: [],
        transition: this.refTransitionClass,
        sending: false,
        isRegistering: false,
        mountedStepLength: null,
        showInitialModal: this.refShowInitialModal,
        followIcon: {
          style: '',
          className: {
            parent: 'js-follow-parent',
            item: 'js-follow',
            nextStep: 'js-follow-next-step',
          },
        },
        showCover: true,
        showPostalAnnotation: false,
        remainingPostalLength: 7,
        showAddressField: false,
        showNameAnnotation: false,
        showTelAnnotation: false,
        showIntroducerNameAnnotation: false,
        remainingPhoneLength: 11,
        postalCityId: null,
        scrollY: 0,
        defaultPageHeight: 0,
      };
    },

    mounted() {
      this.$http.interceptors.response.use((response) => {
        if (response.status === 200) {
          if (response.request.responseURL.match(/api\/validate/)) {
            this.showCover = true;
          }
        }

        return response;
      });

      this._initialize();
      window.addEventListener('scroll', this._handleScroll);
    },

    watch: {
      steps() {
        this._setMountedStepLength();
        this._removeStepOverLength();
      },
    },

    computed: {
      /**
       * 現在のステップ
       *
       * @return {Object}
       */
      activeStep() {
        return this.steps[this.activeIndex - 1];
      },

      /**
       * 現在のステップの「次へ進む」または「」ボタンに付与するGA向けクラス
       *
       * @return {string}
       */
      activeGaClassName() {
        return this.refGaClassNames[this.activeIndex - 1];
      },

      /**
       * 現在のタイトル
       *
       * @return {string}
       */
      activeTitle() {
        return this.activeStep ? this.activeStep.title : '';
      },

      /**
       * 最初のステップかどうか
       *
       * @returns {boolean}
       */
      isFirstStep() {
        return this.activeIndex === 1;
      },

      /**
       * 2番目のステップかどうか
       *
       * @returns {boolean}
       */
      isSecondStep() {
        return this.activeIndex === 2;
      },

      /**
       * 最後のステップかどうか
       *
       * @returns {boolean}
       */
      isLastStep() {
        return this.activeIndex === this.stepLength;
      },

      /**
       * 現在のステップ内のフォームが全てバリデーションをパスしているかどうか
       *
       * @returns {boolean}
       */
      isPassValidateInActiveStep() {
        return this.isPassValidateInStep(this.activeStep);
      },

      /**
       * ステップの長さ
       */
      stepLength() {
        return this.$root.$util.getLength(this.steps);
      },

      /**
       * バリデーションのエラーがある一番手前のステップ取得
       */
      firstStepHasFailValidate() {
        let index = false;

        _.forEach(this.steps, (step, key) => {
          if (!this.isPassValidateInStep(step)) {
            index = key + 1;
          }
        });

        return index;
      },

      /**
       * 初期表示時のステップ位置
       * refFirstActiveIndexが表示するステップ数よりも大きい値だった場合は1を設定する
       */
      firstActiveIndex() {
        return (this.refFirstActiveIndex <= this.stepLength) ? this.refFirstActiveIndex : 1;
      },

      /**
       * 現在のステップ内のフォームバリデーションがパスしているかどうかのリスト
       * @returns {boolean}
       */
      _validatePassedsInActiveStep() {
        const formNames = this.activeStep ? this.activeStep.formNames : [];

        const validates = {};

        _.forEach(formNames, (formName) => {
          validates[formName] = this.isValidatePasses(formName);
        });

        return validates;
      },

      /**
       * 現在のステップで cover を見せるか
       * @returns {boolean}
       */
      showCoverInActiveStep() {
        const showFlag = this.isPassValidateInActiveStep && this.showCover;
        if (showFlag) {
          this._scrollIntoViewByClassName('js-scrollToCta');
        }
        return showFlag;
      },

      /**
       * 固定ステップバーにするかどうか
       * @returns {boolean}
       */
      isFixedStepBar() {
        if (this.scrollY === 0) {
          return true;
        }
        const footerRectHeight = this._firstBoundingClientRectByClassName('l-footer') ?
          this._firstBoundingClientRectByClassName('l-footer').height :
          80;

        if (!footerRectHeight) {
          return false;
        }
        return this.defaultPageHeight - footerRectHeight - this.scrollY - window.innerHeight > 0;
      },
    },

    methods: {
      /**
       * 次のステップに行く
       */
      nextStep() {
        if (this.isLastStep) {
          return;
        }

        this.transition = `${this.refTransitionClass}--next`;
        this.activeIndex += 1;

        _.defer(() => {
          document.body.scrollIntoView();
          this.defaultPageHeight = this._firstBoundingClientRectByClassName('l-wrapper').height;
        });
      },

      /**
       * 前のステップに戻る
       */
      backStep() {
        if (this.isFirstStep) {
          return;
        }

        this.showCover = false;
        this.transition = `${this.refTransitionClass}--back`;
        this.activeIndex -= 1;
      },

      /**
       * ステップ内のバリデーショングループでバリデーションチェック
       *
       * @param step
       */
      checkValidateInStep(step) {
        const postData = this.getValidateValues(step ? step.formNames : []);
        this.sending = true;

        return this.$store.dispatch('validate', {data: postData, path: this.refFormUrl}).then((responseData) => {
          if(_.isEqual(postData,responseData)){
            this.nextStep();
          }
          _.defer(() => {
            this.sending = false;
          });
        }).catch((error) => {
          _.defer(() => {
            this.sending = false;
          });

          return error;
        });
      },

      /**
       * 現在のステップ内のフォーム値にエラーが存在するかどうか
       *
       * @returns {boolean}
       */
      isPassValidateInStep(step) {
        const formNames = step ? step.formNames : [];
        let isPassValidate = true;

        _.forEach(formNames, (formName) => {
          if (!this.isValidatePasses(formName)) {
            isPassValidate = false;
          }
        });

        return isPassValidate;
      },

      /**
       * バリデーション用のチェックボックス値を取得
       *
       * @returns {Object}
       */
      getValidateCheckValues(parent, child) {
        const parentValues = this.$store.getters.getValueByKey(parent, []) || [{}];
        const validateValues = {};

        _.each(parentValues, (value, key) => {
          if (_.isEmpty(value)) {
            return;
          }

          const name = _.trimEnd([parent, key, child].join('.'));
          if (_.isObject(value)) {
            if (Object.prototype.hasOwnProperty.call(value, child)) {
              validateValues[name] = value[child];
            }
            return;
          }

          validateValues[name] = value;
        });

        if (this.$root.$util.getLength(validateValues) === 0) {
          validateValues[[parent, 0, child].join('.')] = null;
        }

        return validateValues;
      },

      /**
       * バリデーション用の値を取得
       *
       * @returns {Object}
       */
      getValidateValues(formNames) {
        const validateValues = {};

        _.forEach(formNames, (formName) => {
          if (this._hasAsteriskInKey(formName)) {
            const [parent, child] = this._divideNamesByAsterisk(formName);
            _.assign(validateValues, this.getValidateCheckValues(parent, child));
          } else {
            validateValues[formName] = this.$store.getters.getValueByKey(formName,
                                                                         null);
          }
        });

        return validateValues;
      },

      /**
       * 指定したキーの値がバリデーションをパスしているかどうか
       *
       * @returns {boolean}
       */
      isValidatePasses(key) {
        if (this._hasAsteriskInKey(key)) {
          const [parent, child] = this._divideNamesByAsterisk(key);
          const isValidate = this.$store.getters.getIsPassValidateByKey(parent, []);

          return _.includes(_.map(isValidate, child), true)
            && !_.includes(_.map(isValidate, child), false);
        }

        return this.$store.getters.getIsPassValidateByKey(key, false);
      },

      /**
       * ステップ内のバリデーションが全てOKの場合次のステップに進む
       */
      nextStepValidationTrue() {
        return this.checkValidateInStep(this.activeStep);
      },
      /**
       * 指定したキーの値がエラー状態か
       * @param {string} key
       * @returns {boolean}
       */
      hasErrorByKey(key) {
        // 現状アスタリスクのキーには対応していません
        const errorMessages = this.$store.getters.getErrorByKey(key);
        return this.$root.$util.getLength(errorMessages) > 0;
      },

      /**
       * 送信時に全体のバリデーションをチェックしてから送信
       * バリデーションエラーがある場合は、バリデーションエラーがあるステップに移動
       */
      submitCheckValidate(event) {
        // 最終ステップのバリデーションを先に実行（v-model.lazyを発火させるため）
        this.checkValidateInStep(this.activeStep).then(() => {
          // step5のバリデーションが成功したら全体のバリデーションを実行
          const postData = this.getValidateValues(_.flatten(_.map(this.steps, 'formNames')));
          this.sending = true;
          this.isRegistering = true;

          this._checkValidate()
            .then((responseData) => {
              if(_.isEqual(postData,responseData)) {
                // TODO: 項目の値自体が変わってしまうので別の方法を採用したい
                // 参考: https://lvgs.slack.com/archives/C011H8Q03NX/p1591951259156800?thread_ts=1591868468.134300&cid=C011H8Q03NX
                const birth = document.getElementsByName('form_cr_c1[birth]')[0];

                if (birth.value.length === 4) {
                  birth.value = `${birth.value}-01-01`;
                }

                event.target.submit();
              }else{
                this.activeIndex = this.firstStepHasFailValidate;
                this.sending = false;
                this.isRegistering = false;
              }
            })
            .catch(() => {
              this.activeIndex = this.firstStepHasFailValidate;
              this.sending = false;
              this.isRegistering = false;
            });
        }).catch(() => {
          // step5のバリデーションに失敗した場合は何もしない
          this.sending = false;
          this.isRegistering = false;
        });
      },

      /**
       * Ajaxでフォーム送信
       */
      submitByAjax(event) {
        this.sending = true;
        this.isRegistering = true;

        this._checkValidate()
          .then(() => {
            const data = new FormData(event.target);

            this.$http.post(this.refFormUrl, data)
              .then(() => {
                this.$emit('submitted');
              }).catch(() => {
                this.sending = false;
                this.isRegistering = false;
              });
          })
          .catch(() => {
            this.activeIndex = this.firstStepHasFailValidate;
            this.sending = false;
            this.isRegistering = false;
          });
      },

      toggleInitialModal() {
        this.showInitialModal = !this.showInitialModal;
      },

      /**
       * 全体のバリデーションをチェックする
       * @private
       */
      _checkValidate() {
        const formNames = _.flatten(_.map(this.steps, 'formNames'));

        return this.$store.dispatch('validateManual', {data: this.getValidateValues(formNames), path: this.refFormUrl});
      },

      /**
       * アスタリスクを含むドット繋ぎの文字列をアスタリスクで分割
       * @private
       */
      _divideNamesByAsterisk(names) {
        const segments = names.split('*');

        const parent = _.trim(segments[0], '.');
        const child = _.trim(segments[1], '.');

        return [parent, child];
      },

      /**
       * アスタリスクを含むドット繋ぎの文字列をアスタリスクで分割
       * @private
       */
      _hasAsteriskInKey(key) {
        return _.includes(key.split('.'), '*');
      },

      /**
       * スクロール値を取得
       */
      _handleScroll() {
        this.scrollY = window.pageYOffset;
      },

      /**
       * 各種の値を初期化
       * 初期値のバリデーション結果取得、自動ステップ初期化
       * @private
       */
      _initialize() {
        const formNames = _.flatten(_.map(this.steps, 'formNames'));
        this._setFirstActiveIndex();

        this.$store.dispatch('initializeValidate', {data: _.merge(this.$root.$util.objectDot(this.$store.state.value), this.getValidateValues(formNames)), path: this.refFormUrl}).then(() => {
          this._initializeAutostep();
          this._setWatchForAutoStep();
        });

        if (this.refShowFollowIcon) {
          this._setWatchForFollowIcon();
        }

        if (this.refAutoScrollOnStepChange) {
          this._setWatchForAutoScrollOnStepChange();
        }

        if (this.refAutoScrollToNextBtn) {
          this._setWatchForAutoScrollToNextBtn();
        }

        if (this.refActiveStepBars) {
          this._setWatchForActiveStepBars();
        }

        this.showAddressField = this.refShowAddressField;
      },

      /**
       * 初期表示時のステップ位置を変更する。
       * @private
       */
      _setFirstActiveIndex() {
        this.activeIndex = this.firstActiveIndex;
      },

      /**
       * マウントされているステップ数をmountedStepLengthにセット
       * @private
       */
      _setMountedStepLength() {
        this.mountedStepLength = this.$children
          .filter((child) => child.$options.name === 'v-form-step')
          .length;
      },

      /**
       * 指定されたステップ数よりもstepsが多い場合、超過分を削除
       * @private
       */
      _removeStepOverLength() {
        if (!this.refShowStepLength) {
          return;
        }

        const overLength = this.stepLength - this.refShowStepLength;
        if (overLength > 0) {
          this.steps.splice(this.refShowStepLength, overLength);
        }
      },

      /**
       * 初期バリデーション時にステップ内の値が全てバリデーションに通っている場合、そのステップの自動ステップをfalseにする
       * @private
       */
      _initializeAutostep() {
        _.forEach(this.steps, (step) => {
          if (this.isPassValidateInStep(step)) {
            _.set(step, 'autoStep', false);
          }
        });
      },

      /**
       * 自動ステップをするために監視する値をセット
       * @private
       */
      _setWatchForAutoStep() {
        this.$watch('isPassValidateInActiveStep', (value) => {
          if (value && _.get(this.activeStep, 'autoStep', false)) {
            _.set(this.activeStep, 'autoStep', false);
            this.nextStep();
          }
        });
      },

      /**
       * ステップが切り替わった時に、自動スクロールするために監視する値をセット
       * @private
       */
      _setWatchForAutoScrollOnStepChange() {
        this.$watch('activeIndex', () => {
          this.$nextTick(() => {
            this._scrollIntoViewByClassName('js-stepFormPosition');
          });
        });
      },

      /**
       * * ステップでカバーを表示する際に、合わせてフッターの「つぎへ」ボタンが存在する場所まで自動スクロールする
       * * @private
       * */
      _setWatchForAutoScrollToNextBtn() {
        this.$watch('showCoverInActiveStep', (value) => {
          if (value) {
            _.defer(() => {
              this._scrollIntoViewByClassName(this.followIcon.className.nextStep);
            });
          }
        });
      },

      /**
       * 引数で指定したclass要素がブラウザ表示領域の一番上になるまでスクロールする
       * @private
       */
      _scrollIntoViewByClassName(className) {
        const elements = document.getElementsByClassName(className);
        if (elements.length > 0) {
          elements[0].scrollIntoView();
        }
      },

      /**
       * アイコン位置を調整する
       * @fixme ABテスト用に一時的な実装。表示させたい位置の要素配下にアイコンを移動させる処理に変更してこの処理を無くしたい
       * @private
       */
      adjustFollowIcon() {
        // jQuery等、vue.jsで検知しない描画を待つため、遅延させる
        window.setTimeout(this._moveFollowIcon, 200);
      },

      /**
       * アイコンを表示するために監視する値をセット
       * @private
       */
      _setWatchForFollowIcon() {
        this.$watch('_validatePassedsInActiveStep', () => {
          this._moveFollowIcon();
        });
      },

      /**
       * ステップバーを表示するために監視する値をセット
       * @private
       */
      _setWatchForActiveStepBars() {
        this.$watch('_validatePassedsInActiveStep', (n) => {
          const selected = [];
          this.activeStep.formNames.forEach((formName) => {
            if (n[formName] && formName !== 'postal' && formName !== 'form_cr_c1.email') {
              selected.push(true);
            }
          });
          this.selectedList[this.activeIndex] = selected;
          this._activeStepBars();
        });
      },

      /**
       * is-activeを適用するかの判定用の配列
       */
      _activeStepBars() {
        const steps = [];
        for (let i = 1; i <= this.activeIndex; i++) {
          const selected = this.selectedList[i];
          _.forEach(selected, (value) => {
            if (value) {
              steps.push(true);
            }
          });
        }
        this.activeSteps = steps;
      },

      /**
       * アイコンを移動する
       * 親要素からの縦absolute px
       * @private
       */
      _moveFollowIcon() {
        this.$nextTick(() => {
          const toRect = this._firstBoundingClientRectByClassName(
            this.isPassValidateInActiveStep ? this.followIcon.className.nextStep
            : this.followIcon.className.item
          );
          const parentRect = this._firstBoundingClientRectByClassName(
            this.followIcon.className.parent
          );

          if (toRect && parentRect) {
            const adjustPx = this.isPassValidateInActiveStep ? -15 : -30;// デザイン的にアイコンの位置を調整する値

            const topPx = _.get(toRect, 'top') - _.get(parentRect, 'top') + adjustPx;
            this.followIcon.style = `transform: translateY(${topPx}px)`;

            this._scrollIntoFollowIcon();
          } else {
            this.followIcon.style = '';
          }
        });
      },

      /**
       * クラス名から取得した要素1つ目の位置情報オブジェクトを返す
       * @private
       */
      _firstBoundingClientRectByClassName(className) {
        const elements = document.getElementsByClassName(className);

        if (elements.length > 0) {
          return elements[0].getBoundingClientRect();
        }

        return null;
      },

      /**
       *
       * @private
       */
      _scrollIntoFollowIcon() {
        if (this.isPassValidateInActiveStep) {
          return;
        }

        const elements = document.getElementsByClassName(this.followIcon.className.item);

        if (elements.length > 0) {
          if (elements[0].dataset.scrollTo) {
            elements[0].scrollIntoView();
          }
        }
      },

      /**
       * 前のステップに戻る
       */
      backStepWithCover() {
        this.backStep();
      },

      /**
       * 次のステップに進む
       */
      nextStepWithCover() {
        this.nextStepValidationTrue().then((error) => {
          this.showCover = true;
        });
      },

      /**
       * cover を削除する
       */
      removeCover() {
        this.showCover = false;
      },

      /**
       * 都道府県,市区町村入力欄の表示/非表示切り替えに伴う
       */
      adjustFollowIconByToggleAddressField() {
        this.showAddressField = !this.showAddressField;
        this.adjustFollowIcon();
      },

      onKeyupBirth($event) {
        const { currentTarget } = $event;

        if (currentTarget.value.length === 4) {
          const data = {'form_cr_c1.birth': currentTarget.value};

          this.$store.commit('setValue', data);
          this.$store.dispatch('validate', {path: this.refFormUrl, data}).then(() => {
            currentTarget.blur();
          });
        }
      },

      /**
       * 電話番号の残り入力文字数
       *
       * @param $event
       */
      onKeyupPhone($event) {
        const { currentTarget } = $event;

        this.remainingPhoneLength = 11 - currentTarget.value.length;

        if (this.remainingPhoneLength === 0) {
          const data = {'form_cr_c1.phone': currentTarget.value};

          this.$store.commit('setValue', data);
          this.$store.dispatch('validate', {path: this.refFormUrl, data}).then(() => {
            currentTarget.blur();
          });
        }
      },

      /**
       * TODO: cancelToken 対応をとりあえずここで行っている
       */
      _validate(data, cancelTokenSource) {
        const params = {path: '/lp/entry/input/', data};
        const options = {cancelToken: cancelTokenSource.token};

        return this.$http.post('/api/validate/', params, options).then(() => {
          _.defer(() => {
            _.each(data, (value, key) => {
              this.$store.commit('deleteError', key);
            });

            this.$store.commit('setPassValidate', {keys: _.keys(data)});
          });
        }).catch((error) => {
          _.defer(() => {
            if (_.get(error, 'response.status', null) === 422) {
              const messages = _.get(error, 'response.data.errors', null);

              this.$store.commit('setError', messages);
              this.$store.commit('setFailValidate', {keys: _.keys(messages)});
              this.$store.commit('setPassValidate', {keys: _.difference(_.keys(data), _.keys(messages))});
            }
          });
        });
      },

      /**
       * fetchMaster による fetch 後のバリデーション完了を待ってから実行する
       */
      onCityIdValidated() {
        if (!this.postalCityId) {
          return;
        }

        const data = {
          'form_cr_c1.city_id': this.postalCityId,
        };

        this.$store.commit('setValue', data);
        this.$store.dispatch('validate', {path: this.refFormUrl, data});

        this.postalCityId = null;
      },

      /**
       * 郵便番号の残り入力文字数
       *
       * @param $event
       */
       onKeyupPostal($event) {
        const { currentTarget } = $event;

        this.remainingPostalLength = 7 - currentTarget.value.length;

        if (this.remainingPostalLength === 0) {
          this.$http.get('/api/postal/get', {params: {code: currentTarget.value}})
            .then((res) => {
              if (res.data.length > 0) {
                currentTarget.blur();

                const postal = res.data[0];

                if (!this.refTargetPrefectures) {
                  return;
                }

                const targetPrefectureIds = this.refTargetPrefectures.map(
                  (prefecture) => prefecture.id
                );

                if (!targetPrefectureIds.includes(postal.prefecture_id)) {
                  if (!this.showAddressField) {
                    document.getElementById('j-addresses').click();
                    this.showAddressField = true;
                  }
                  return;
                }

                const isChangedPrefectureId = parseInt(this.getStoreValue('form_cr_c1.prefecture_id'), 10) !== postal.prefecture_id;

                const data = {
                  'form_cr_c1.prefecture_id': postal.prefecture_id,
                };

                this.postalCityId = postal.city_id;

                this.$store.commit('setValue', data);
                this.$store.dispatch('validate', {path: this.refFormUrl, data});

                if (!isChangedPrefectureId) {
                  this.onCityIdValidated();
                }
              }
            });
        }
      },
    },
  });
</script>
